import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { uk } from 'date-fns/locale';
import { BaseDialog } from '@components/common/BaseDialog.tsx';
import { toastService } from '@services/toast.service';
import { toLocalIso } from '@/utils/datetime';
import {
  collaborationPlanService,
  CollaborationStep,
  CollaborationStepOption,
} from '@/services/collaboration-plan.service';
import { COLLABORATION_STEP_VISUALS } from './collaborationStepIcons';
import { withDefaultTime } from './collaborationStepSchedule';

interface CollaborationStepFormDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: number | string;
  /** Задано — редагування (форма та сама, що й додавання, лише передзаповнена). Відсутнє/null — додавання. */
  editingStep?: CollaborationStep | null;
  onSaved: () => void;
}

const combineDateTime = (date: Date, time: Date): Date => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined;
};

/** Форма "Додати/Редагувати задачу в план співпраці" (Figma, 2026-10-29). "Назва задачі" -
 *  `Autocomplete freeSolo`: обрано з каталогу (`collaborationPlanService.getOptions()`) -
 *  формалізована задача (`type`, без `title`); вільний текст - довільна (`title`, без `type`).
 *  Дата/час - необов'язкові (задача без дедлайну не синхронізується в календар, `syncCalendar`
 *  на бекенді) - за замовчуванням порожні, користувач заповнює свідомо, не через незняту дату
 *  "сьогодні 8:00". Дата без часу - 09:00 (як у "Заготовці"); час без дати - не зберігається.
 *  Обидва поля `clearable` - можна прибрати вже введену дату/час. */
export const CollaborationStepFormDialog: React.FC<CollaborationStepFormDialogProps> = ({
  open, onClose, clientId, editingStep, onSaved,
}) => {
  const isEdit = !!editingStep;
  const [options, setOptions] = useState<CollaborationStepOption[]>([]);
  const [fieldValue, setFieldValue] = useState<CollaborationStepOption | string | null>(null);
  // Controlled: freeSolo keeps its own typed text when value is reset to null (clearOnBlur is off).
  const [inputValue, setInputValue] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    collaborationPlanService.getOptions().then(setOptions).catch((err) => {
      console.error('CollaborationStepFormDialog: getOptions failed', err);
      toastService.error('Не вдалося завантажити каталог типових задач');
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editingStep) {
      const matchedOption = options.find((o) => o.typeCode === editingStep.type);
      setFieldValue(matchedOption ?? editingStep.title ?? '');
      setInputValue(matchedOption?.label ?? editingStep.title ?? '');
      if (editingStep.scheduledLdt) {
        const scheduled = new Date(editingStep.scheduledLdt);
        setDate(scheduled);
        setTime(scheduled);
      } else {
        setDate(null);
        setTime(null);
      }
    } else {
      setFieldValue(null);
      setInputValue('');
      setDate(null);
      setTime(null);
    }
  }, [open, editingStep, options]);

  const selectedOption = fieldValue && typeof fieldValue === 'object' ? fieldValue : null;
  const freeText = typeof fieldValue === 'string' ? fieldValue.trim() : '';
  // Time alone has no day to attach to.
  const timeWithoutDate = !!time && !date;
  const isValid = (!!selectedOption || !!freeText) && !timeWithoutDate;
  // Вільний текст -> тип CUSTOM (та сама логіка, що на бекенді - resolveTypeAndTitle), тож поки
  // нічого не обрано/введено - показуємо іконку CUSTOM як дефолт типової іконки поля.
  const fieldVisual = COLLABORATION_STEP_VISUALS[selectedOption?.typeCode ?? 'custom'];

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      // Date without time -> default hour, as in the preset dialog.
      const scheduledLdt = date ? toLocalIso(time ? combineDateTime(date, time) : withDefaultTime(date)) : undefined;
      const input = selectedOption
        ? { type: selectedOption.typeCode, scheduledLdt }
        : { title: freeText, scheduledLdt };

      if (isEdit) {
        await collaborationPlanService.updateStep(clientId, editingStep!.id, input);
        toastService.success('Задачу оновлено');
      } else {
        await collaborationPlanService.createStep(clientId, input);
        toastService.success('Задачу додано');
      }
      onSaved();
      onClose();
    } catch {
      toastService.error(isEdit ? 'Не вдалося оновити задачу' : 'Не вдалося додати задачу');
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Редагувати задачу' : 'Додати в план співпраці'}
      maxWidth={false}
      paperSx={{ width: 400, borderRadius: '16px' }}
      titleSx={{ fontSize: 20, lineHeight: '23px', fontWeight: 600, color: '#26313F' }}
      contentSx={{ p: 3, borderBottom: 'none' }}
      actions={
        <Button
          onClick={handleSave}
          disabled={!isValid || saving}
          sx={{
            bgcolor: '#6EB52C', color: '#FFFFFF', borderRadius: '6px', minWidth: 118,
            height: 40, px: 2, fontSize: 14, fontWeight: 500, textTransform: 'none',
            boxShadow: 'none', '&:hover': { bgcolor: '#6EB52C', boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: '#D5D7D4', color: '#FFFFFF' },
          }}
        >
          {isEdit ? 'Зберегти' : 'Призначити'}
        </Button>
      }
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={uk}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 12, lineHeight: '14px', fontWeight: 500, color: '#666666', mb: '6px' }}>
              Назва задачі *
            </Typography>
            <Autocomplete
              freeSolo
              options={options}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              isOptionEqualToValue={(option, value) => typeof value === 'object' && value !== null && option.typeCode === value.typeCode}
              value={fieldValue}
              inputValue={inputValue}
              onChange={(_, newValue) => setFieldValue(newValue)}
              onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason === 'input') setFieldValue(newInputValue);
              }}
              renderOption={(props, option) => {
                const visual = COLLABORATION_STEP_VISUALS[option.typeCode];
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: visual.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <visual.Icon sx={{ fontSize: 16, color: visual.color }} />
                    </Box>
                    {option.label}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Оберіть зі списку або введіть свою"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '4px' }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '4px', bgcolor: fieldVisual.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <fieldVisual.Icon sx={{ fontSize: 14, color: fieldVisual.color }} />
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, lineHeight: '14px', fontWeight: 500, color: '#666666', mb: '6px' }}>
              Дата
            </Typography>
            <DatePicker
              value={date}
              onChange={setDate}
              format="dd.MM.yyyy"
              slotProps={{
                textField: { fullWidth: true, error: timeWithoutDate, helperText: timeWithoutDate ? 'Вкажіть дату для цього часу' : undefined },
                field: { clearable: true },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, lineHeight: '14px', fontWeight: 500, color: '#666666', mb: '6px' }}>
              Час
            </Typography>
            <TimePicker
              value={time}
              onChange={setTime}
              ampm={false}
              slotProps={{ textField: { fullWidth: true }, field: { clearable: true } }}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    </BaseDialog>
  );
};
